import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  useTheme,
  FormHelperText,
  Chip,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fetchBranches, fetchUserProfiles } from "../../../../../utils/Apis";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const BranchEditModal = ({
  open,
  onClose,
  onSubmit,
  mode = "add",
  branchId,
}) => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.data.branches?.branches || []);
  const adminUsers = useSelector((state) => state.data.adminUsers?.items || []);
  const loading = useSelector((state) => state.data.loading);
  const error = useSelector((state) => state.data.dataError);

  const theme = useTheme();

  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    location: {
      address: "",
      city: "",
      state: "",
      zip: "",
      coordinates: [],
    },
    contact: {
      phone: "",
      email: "",
    },
    adminIds: [],
  });

  const existingBranch = branches.find((branch) => branch._id === branchId);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && branchId && existingBranch) {
        setFormData({
          name: existingBranch.name || "",
          location: existingBranch.location || {
            address: "",
            city: "",
            state: "",
            zip: "",
            coordinates: [],
          },
          contact: existingBranch.contact || "",
          adminIds: (existingBranch.adminIds || []).map((id) =>
            typeof id === "object" ? id._id : id
          ),
        });
      } else if (mode === "add") {
        setFormData({
          name: "",
          location: {
            address: "",
            city: "",
            state: "",
            zip: "",
            coordinates: [],
          },
          contact: { phone: "", email: "" },
          adminIds: [],
        });
      }

      if (!adminUsers || adminUsers.length === 0) {
        dispatch(fetchUserProfiles({ filters: { role: "admin" } }));
      }
    }
  }, [open, mode, branchId, existingBranch, dispatch, adminUsers]);

  const validateForm = () => {
    const errors = {};
    const { name, location, contact } = formData;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!name.trim()) errors.name = "Branch name is required";
    if (!location.address.trim()) errors.address = "Address is required";
    if (!location.city.trim()) errors.city = "City is required";
    if (!location.state.trim()) errors.state = "State is required";
    if (!location.zip.trim()) errors.zip = "Zip code is required";

    if (!contact.phone.trim()) {
      errors.phone = "Contact number is required";
    } else if (!phoneRegex.test(contact.phone)) {
      errors.phone = "Phone number must be 10 digits";
    }

    // Zip code validation: must be a 6-digit number
    if (!/^\d{6}$/.test(location.zip)) {
      errors.zip = "Zip code must be a 6-digit number";
    }

    if (!contact.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(contact.email)) {
      errors.email = "Invalid email format";
    }

    if (
      location.coordinates.length < 2 ||
      !location.coordinates[0] ||
      !location.coordinates[1]
    ) {
      errors.coordinates = "Latitude and Longitude are required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e, path) => {
    const { name, value } = e.target;

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === "adminIds") {
      setFormData((prev) => ({
        ...prev,
        adminIds: typeof value === "string" ? value.split(",") : value,
      }));
      return;
    }

    if (path === "location") {
      setFormData((prev) => ({
        ...prev,
        location: { ...prev.location, [name]: value },
      }));
    } else if (path === "contact") {
      setFormData((prev) => ({
        ...prev,
        contact: { ...prev.contact, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCoordinatesChange = (e, index) => {
    const value = e.target.value;
    const coords = [...formData.location.coordinates];
    coords[index] = value;
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, coordinates: coords },
    }));
    setFormErrors((prev) => ({ ...prev, coordinates: null }));
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const submissionData = {
      ...formData,
      id: mode === "edit" ? branchId : undefined,
    };

    onSubmit(submissionData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {mode === "edit" ? "Edit Branch Details" : "Add New Branch"}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              label="Branch Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />
            <TextField
              label="Address"
              name="address"
              value={formData.location.address}
              onChange={(e) => handleChange(e, "location")}
              fullWidth
              error={!!formErrors.address}
              helperText={formErrors.address}
              required
            />
            <TextField
              label="City"
              name="city"
              value={formData.location.city}
              onChange={(e) => handleChange(e, "location")}
              fullWidth
              error={!!formErrors.city}
              helperText={formErrors.city}
              required
            />
            <TextField
              label="Zip Code"
              name="zip"
              value={formData.location.zip}
              onChange={(e) => handleChange(e, "location")}
              fullWidth
              error={!!formErrors.zip}
              helperText={formErrors.zip}
              required
            />

            <TextField
              label="State"
              name="state"
              value={formData.location.state}
              onChange={(e) => handleChange(e, "location")}
              fullWidth
              error={!!formErrors.state}
              helperText={formErrors.state}
              required
            />
            <TextField
              label="Contact Number"
              name="phone"
              value={formData.contact.phone}
              onChange={(e) => handleChange(e, "contact")}
              fullWidth
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              required
            />
            <TextField
              label="Email"
              name="email"
              value={formData.contact.email}
              onChange={(e) => handleChange(e, "contact")}
              fullWidth
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
            />

            <FormControl fullWidth error={!!formErrors.adminIds} required>
              <InputLabel id="admin-select-label">Assign Admin</InputLabel>
              <Select
                labelId="admin-select-label"
                name="adminIds"
                multiple
                value={formData.adminIds}
                onChange={handleChange}
                input={<OutlinedInput label="Assign Admin" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((adminId) => {
                      const user = adminUsers.find(
                        (u) => u.id === adminId || u._id === adminId
                      );

                      const label = user
                        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                        : typeof adminId === "object"
                          ? adminId.email || "Unknown"
                          : String(adminId);

                      return <Chip key={label} label={label} />;
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {adminUsers.length > 0 ? (
                  adminUsers.map((admin) => (
                    <MenuItem
                      key={admin.id || admin._id}
                      value={admin.id || admin._id}
                    >
                      <ListItemText
                        primary={`${admin.firstName || ""} ${admin.lastName || ""}`.trim()}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Loading admins...</MenuItem>
                )}
              </Select>
              {formErrors.adminIds && (
                <FormHelperText>{formErrors.adminIds}</FormHelperText>
              )}
            </FormControl>

            <Box display="flex" gap={2}>
              <TextField
                label="Latitude"
                name="latitude"
                value={formData.location.coordinates[1] || ""}
                onChange={(e) => handleCoordinatesChange(e, 1)}
                fullWidth
                error={!!formErrors.coordinates}
                helperText={formErrors.coordinates}
                required
              />
              <TextField
                label="Longitude"
                name="longitude"
                value={formData.location.coordinates[0] || ""}
                onChange={(e) => handleCoordinatesChange(e, 0)}
                fullWidth
                error={!!formErrors.coordinates}
                helperText={formErrors.coordinates}
                required
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : mode === "edit" ? (
            "Save Changes"
          ) : (
            "Add Branch"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchEditModal;
